import { useValue } from "@legendapp/state/react";
import { Button } from "@repo/ui/button";
import { Icon } from "@repo/ui/components/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@repo/ui/ui/dropdown-menu";
import { Spinner } from "@repo/ui/ui/spinner";
import { useNavigate } from "@tanstack/react-router";

import { activeThread$, activeThreadId$, threads$ } from "~/stores/threads";

export type ChatHeaderProps = {
  isBusy: boolean;
  hasMessages: boolean;
  onNewChat: () => void;
  onSelectThread: (id: string) => void;
  /** Shows the "expand to full page" button (right pane only). */
  showExpand?: boolean;
};

export const ChatHeader = ({
  isBusy,
  hasMessages,
  onNewChat,
  onSelectThread,
  showExpand = false,
}: ChatHeaderProps) => {
  const navigate = useNavigate();
  const threadId = useValue(activeThreadId$);

  // Navigating mid-stream would unmount the pane's `useChat` instance and drop
  // the in-flight stream (and its post-turn re-sync), so this is gated on
  // `isBusy` at the button. History carries the return location for the page's
  // back button.
  const handleExpand = () => {
    const id = activeThreadId$.get();
    if (!id) return;
    navigate({
      to: "/thread/$threadId",
      params: { threadId: id },
    });
  };
  const threadTitle = useValue(() => activeThread$.get()?.title);
  const threadsList = useValue(() => {
    const map = threads$.get();
    return map ? Object.values(map) : [];
  });

  return (
    <div className="flex h-(--header-height) shrink-0 items-center justify-between border-b px-2">
      <div className="flex h-7 min-w-0 items-center gap-1 rounded-md bg-muted px-2">
        {isBusy && (
          <Spinner className="size-3 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate font-semibold text-foreground text-xs">
          {threadTitle || "New thread"}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-0.5 pl-2">
        {showExpand && (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleExpand}
                  disabled={isBusy || !threadId}
                  aria-label="Expand to full page"
                >
                  <Icon name="arrow-up-left-down-right" />
                </Button>
              }
            />
            <TooltipContent>Expand to full page</TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                onClick={onNewChat}
                disabled={isBusy || !hasMessages}
                aria-label="New chat"
              >
                <Icon name="plus" />
              </Button>
            }
          />
          <TooltipContent>Start a new thread</TooltipContent>
        </Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                aria-label="Switch thread"
                disabled={isBusy}
              >
                <Icon name="chevron-down" />
              </Button>
            }
          />
          <DropdownMenuContent align="end" className="w-48">
            {threadsList.length === 0 ? (
              <p className="px-2 py-1.5 text-muted-foreground text-xs">
                No threads yet
              </p>
            ) : (
              <DropdownMenuRadioGroup
                value={threadId}
                onValueChange={(value) => {
                  // Switching mid-stream would leave the stream running against
                  // the old thread while the UI shows the new one; block it.
                  if (isBusy) return;
                  onSelectThread(value);
                }}
              >
                {threadsList.map((thread) => (
                  <DropdownMenuRadioItem key={thread.id} value={thread.id}>
                    <span className="block max-w-48 truncate">
                      {thread.title || "New thread"}
                    </span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
